<script setup>
import { computed } from 'vue'
import { useNavRailExpanded } from '../composables/useNavRail'

const props = defineProps({
  icon: String,
  label: { type: String, required: true },
  to: [String, Object],
  href: String,
  external: Boolean,
  active: Boolean,
  variant: { type: String, default: 'default' },
  disabled: Boolean,
  pulse: Boolean,
})

const emit = defineEmits(['click'])
const expanded = useNavRailExpanded()
const resolvedIcon = computed(() => props.icon || 'circle')
const classes = computed(() => ({
  active: props.active,
  expanded: expanded.value,
  disabled: props.disabled,
  pulse: props.pulse,
  [`variant-${props.variant}`]: props.variant !== 'default',
}))

function click(event) {
  if (props.disabled) {
    event.preventDefault()
    return
  }
  emit('click', event)
}
</script>

<template><component
    :is="to ? 'RouterLink' : href ? 'a' : 'button'"
    :to="to"
    :href="href"
    :target="href && external ? '_blank' : undefined"
    :rel="href && external ? 'noopener noreferrer' : undefined"
    :type="!to && !href ? 'button' : undefined"
    :disabled="!to && !href ? disabled : undefined"
    class="nav-rail-item"
    :class="classes"
    :aria-label="label"
    @click="click"
  >
    <span class="nav-rail-indicator" />
    <span class="nav-rail-icon">
      <slot name="icon"><v-icon :icon="resolvedIcon" size="20" /></slot>
    </span>
    <span class="nav-rail-label">{{ label }}</span>
    <v-tooltip v-if="!expanded" activator="parent" location="end" content-class="nav-rail-tooltip">
      {{ label }}
    </v-tooltip>
  </component></template>

<style scoped>
.nav-rail-item {
  --nav-ease: cubic-bezier(.16, 1, .3, 1);
  --nav-duration: .48s;
  position:relative;
  box-sizing:border-box;
  display:flex;
  align-items:center;
  justify-content:center;
  width:100%;
  height:42px;
  min-height:42px;
  max-height:42px;
  margin:0 0 4px;
  padding:0;
  border:1px solid transparent;
  border-radius:12px;
  background:transparent;
  color:var(--nav-item);
  font-size:inherit;
  font-weight:inherit;
  letter-spacing:inherit;
  line-height:1;
  text-align:left;
  text-decoration:none;
  vertical-align:middle;
  cursor:pointer;
  overflow:hidden;
  transition:background .18s ease,border-color .18s ease,color .18s ease}
.nav-rail-item.expanded {
  justify-content:flex-start}
.nav-rail-item:hover:not(.disabled) {
  color:var(--nav-item-hover);
  background:var(--hover-color);
  border-color:var(--line)}
.nav-rail-item.active {
  color:var(--ink);
  background:rgba(var(--v-theme-primary),.18);
  border-color:rgba(var(--v-theme-primary),.35)}
.nav-rail-item.active .nav-rail-indicator {
  opacity:1}
.nav-rail-item.disabled {
  opacity:.45;
  cursor:not-allowed}
.nav-rail-indicator {
  position:absolute;
  left:-15px;
  top:50%;
  transform:translateY(-50%);
  width:3px;
  height:22px;
  border-radius:0 4px 4px 0;
  background:rgb(var(--v-theme-primary));
  opacity:0;
  transition:opacity .2s ease,left var(--nav-duration) var(--nav-ease)}
.nav-rail-item.expanded .nav-rail-indicator {
  left:0}
.nav-rail-icon {
  position:relative;
  display:flex;
  align-items:center;
  justify-content:center;
  align-self:stretch;
  width:42px;
  flex:0 0 42px;
  line-height:0}
.nav-rail-item:not(.expanded) .nav-rail-icon {
  width:100%;
  flex:1 1 auto}
.nav-rail-icon .v-icon, .nav-rail-icon .nr-icon {
  position:relative!important;
  left:auto!important;
  top:auto!important;
  transform:translate(-1px)!important;
  display:block!important;
  width:20px!important;
  height:20px!important;
  min-width:20px!important;
  min-height:20px!important;
  max-width:20px!important;
  max-height:20px!important;
  margin:0!important;
  padding:0!important;
  font-size:20px!important;
  line-height:0!important;
  overflow:hidden!important;
  box-sizing:border-box!important}
.nav-rail-icon .v-avatar {
  --v-avatar-height: 28px;
  display:inline-flex!important;
  align-items:center;
  justify-content:center;
  width:28px!important;
  height:28px!important;
  min-width:28px!important;
  min-height:28px!important;
  margin:0!important;
  transform:none!important;
  flex:none!important;
  overflow:hidden!important;
  position:relative!important;
  border-radius:50%!important}
.nav-rail-icon .nav-persona-avatar {
  display:block;
  width:28px;
  height:28px;
  border-radius:50%;
  object-fit:cover;
  margin:0;
  padding:0;
  flex-shrink:0}
.nav-rail-label {
  display:none;
  min-width:0;
  max-width:140px;
  flex:0 1 auto;
  overflow:hidden;
  white-space:nowrap;
  opacity:0;
  transform:translate(-8px);
  font-size:.78rem;
  font-weight:700;
  letter-spacing:.02em;
  text-align:left;
  line-height:1.2;
  pointer-events:none;
  transition:opacity .2s ease,transform .28s var(--nav-ease)}
.nav-rail-item.expanded .nav-rail-label {
  display:block;
  opacity:1;
  transform:translate(0);
  transition-delay:.12s,.12s}
.nav-rail-item:not(.expanded) .nav-rail-label {
  display:none}
.nav-rail-item.variant-premium {
  color:var(--accent-gold)}
.nav-rail-item.variant-premium:hover:not(.disabled), .nav-rail-item.variant-premium.active {
  background:var(--accent-gold-bg);
  border-color:var(--accent-gold-line);
  color:var(--status-warn)}
.nav-rail-item.pulse.variant-premium {
  color:#ffd54f}
.nav-rail-item.pulse .nav-rail-icon {
  animation:nav-premium-pulse 1.8s ease-in-out infinite}
.nav-rail-item.pulse.variant-wheelspin .nav-rail-icon {
  animation:nav-wheelspin-pulse 1.8s ease-in-out infinite}
@keyframes nav-premium-pulse {
  0%, to {
  color:#ffc107;
  filter:drop-shadow(0 0 2px rgba(255,193,7,.35));
  transform:scale(1)}
35% {
  color:#ffe082;
  filter:drop-shadow(0 0 8px rgba(255,213,79,.85));
  transform:scale(1.1)}
55% {
  color:#fff8e1;
  filter:drop-shadow(0 0 12px rgba(255,236,179,.95));
  transform:scale(1.14)}
75% {
  color:#ffd54f;
  filter:drop-shadow(0 0 5px rgba(255,213,79,.55));
  transform:scale(1.06)}
}@keyframes nav-wheelspin-pulse {
  0%, to {
  color:#42a5f5;
  filter:drop-shadow(0 0 2px rgba(66,165,245,.35));
  transform:scale(1)}
35% {
  color:#64b5f6;
  filter:drop-shadow(0 0 8px rgba(66,165,245,.85));
  transform:scale(1.1)}
55% {
  color:#90caf9;
  filter:drop-shadow(0 0 12px rgba(144,202,249,.95));
  transform:scale(1.14)}
75% {
  color:#42a5f5;
  filter:drop-shadow(0 0 5px rgba(66,165,245,.55));
  transform:scale(1.06)}
}.nav-rail-item.variant-donation {
  color:var(--status-pink)}
.nav-rail-item.variant-donation:hover:not(.disabled), .nav-rail-item.variant-donation.active {
  background:#ff8a801f;
  border-color:#ff8a8047;
  color:var(--status-orange)}
.nav-rail-item.variant-wheelspin {
  color:#42a5f5}
.nav-rail-item.variant-wheelspin:hover:not(.disabled), .nav-rail-item.variant-wheelspin.active {
  background:#2196f31f;
  border-color:#42a5f559;
  color:#64b5f6}
.nav-rail-item.pulse.variant-wheelspin {
  color:#42a5f5}
.nav-rail-item.variant-danger {
  color:var(--status-danger)}
.nav-rail-item.variant-danger:hover:not(.disabled) {
  background:#f443361f;
  border-color:#f4433647;
  color:var(--status-danger)}
@media (prefers-reduced-motion: reduce) {
.nav-rail-item, .nav-rail-label, .nav-rail-indicator {
  transition:none!important}
.nav-rail-item.pulse .nav-rail-icon {
  animation:none!important;
  filter:drop-shadow(0 0 6px rgba(255,213,79,.7));
  color:#ffe082;
  transform:none}
.nav-rail-item.pulse.variant-wheelspin .nav-rail-icon {
  filter:drop-shadow(0 0 6px rgba(66,165,245,.7));
  color:#64b5f6}

}
#menu {
  margin-bottom:10px!important}
</style>
